import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { BoxProps } from '@mui/material';
import { ReactElement, SyntheticEvent, useState } from 'react';

import { useAttachmentDetails } from 'api';
import { ReactComponent as CSVIcon } from 'assets/icons/fileType/csv.svg';
import { ReactComponent as ExcelIcon } from 'assets/icons/fileType/excel.svg';
import { ReactComponent as PPTIcon } from 'assets/icons/fileType/ppt.svg';
import { ReactComponent as TextIcon } from 'assets/icons/fileType/txt.svg';
import { ReactComponent as WordIcon } from 'assets/icons/fileType/word.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/newTrash.svg';
import { theme } from 'styles';
import { handleFileDownload } from 'utils';

import { DocumentW, DocumentNameW, DownloadIconW, OuterW, IconBox } from './styled';

interface DocumentAttachmentProps {
  postId?: string;
  attachmentId: string;
  fileName: string;
  file_extension?: string;
  originalFileUrl: string;
  handleDelete?: () => void;
  hasDownload?: boolean;
  wrapperSx?: BoxProps['sx'];
  originalFileName: string;
  blockDelete?: boolean;
  isPreview?: boolean;
}

export function DocumentAttachment({
  fileName,
  file_extension,
  postId,
  attachmentId,
  handleDelete,
  hasDownload,
  wrapperSx,
  originalFileName,
  originalFileUrl,
  isPreview,
  blockDelete,
}: DocumentAttachmentProps): ReactElement {
  const [isHovered, setIsHovered] = useState(false);
  const { refetch } = useAttachmentDetails({
    postId,
    attachmentId: attachmentId,
    fileName: originalFileName,
    fileUrl: originalFileUrl,
    enabled: false,
  });
  async function handleAttachmentRemove() {
    handleDelete?.();
  }
  async function handleDownloadClick(e: SyntheticEvent) {
    e.stopPropagation();
    const { data } = await refetch();

    if (data?.data && !isPreview) {
      await handleFileDownload({ blobFile: data.data, fileName: originalFileName });
    }
  }

  const ext = file_extension?.split('.').pop()?.toLowerCase() ?? '';
  const iconSx = { width: 20, height: 20 };
  const icon = (() => {
    if (ext === 'pdf') return <PictureAsPdfIcon sx={{ ...iconSx, color: '#e53935' }} />;
    if (ext === 'doc' || ext === 'docx') return <WordIcon />;
    if (ext === 'ppt' || ext === 'pptx') return <PPTIcon />;
    if (ext === 'xls' || ext === 'xlsx') return <ExcelIcon />;
    if (ext === 'csv') return <CSVIcon />;
    if (ext === 'txt') return <TextIcon />;
    return <InsertDriveFileOutlinedIcon sx={{ ...iconSx, color: '#217346' }} />;
  })();

  return (
    <OuterW>
      <DocumentW
        sx={wrapperSx}
        onClick={handleDownloadClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <IconBox>{icon}</IconBox>
        <DocumentNameW>{fileName}</DocumentNameW>
        {hasDownload && (
          <DownloadIconW
            sx={{
              height: '21px',
              width: '21px',
              color: theme.palette.white.main,
              backgroundColor: isHovered ? theme.palette.info.main : theme.palette.primary.main,
            }}
            onClick={handleDownloadClick}
          />
        )}
        {!blockDelete && (
          <DeleteIcon
            onClick={(e) => {
              e.stopPropagation();
              handleAttachmentRemove();
            }}
            style={{ width: '32px', height: 'px', color: theme.palette.primary.grey }}
          />
        )}
      </DocumentW>
    </OuterW>
  );
}
